import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, AuthError } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { LogIn, UserPlus, ShieldCheck, Mail, Lock, AlertCircle } from 'lucide-react';

interface AuthScreenProps {
    onLoginSuccess: () => void;
}

export const AuthScreen: React.FC<AuthScreenProps> = ({ onLoginSuccess }) => {
    const [isLoginView, setIsLoginView] = useState(true);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [teamName, setTeamName] = useState('');
    const [consent, setConsent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const toggleAuthView = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsLoginView(!isLoginView);
        setError(null);
    };

    const getFirebaseAuthErrorMessage = (error: AuthError): string => {
        switch (error.code) {
            case 'auth/invalid-email': return 'Invalid identifier format.';
            case 'auth/user-not-found':
            case 'auth/wrong-password':
            case 'auth/invalid-login-credentials': return 'Incorrect credentials.';
            case 'auth/email-already-in-use': return 'Username unavailable.';
            default: return 'Authentication failed. Please try again.';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setError("Identification required.");
            return;
        }
        if (!isLoginView && (!fullName || !teamName)) {
            setError("Full Name and Team Name are required for registration.");
            return;
        }
        if (!consent) {
            setError("Please accept the terms to proceed.");
            return;
        }
        setError(null);
        setLoading(true);

        const email = `${username}@simulate2.com`;

        try {
            if (isLoginView) {
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                const userDocRef = doc(db, "users", user.uid);
                const userDoc = await getDoc(userDocRef);
                
                if (!userDoc.exists()) {
                    await setDoc(userDocRef, {
                        email: user.email,
                        username: username,
                        consent: true,
                        fullName: username,
                        teamName: "Synced User",
                        displayName: username
                    });
                }
            } else {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                await setDoc(doc(db, "users", user.uid), {
                    email: user.email,
                    username: username,
                    consent: true,
                    fullName: fullName,
                    teamName: teamName,
                    displayName: fullName // Also set standard displayName for convenience
                });
            }
            onLoginSuccess();
        } catch (error) {
            console.error(error);
            setError(getFirebaseAuthErrorMessage(error as AuthError));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ width: '100%', maxWidth: '420px', margin: '0 1rem' }}>
            <div className="card" style={{ padding: '2.5rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ width: '64px', height: '64px', background: 'var(--primary-soft)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', color: 'var(--primary)' }}>
                        {isLoginView ? <LogIn size={32} /> : <UserPlus size={32} />}
                    </div>
                    <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                        {isLoginView ? 'Welcome Back' : 'Get Started'}
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                        {isLoginView ? 'Sign in to access your coaching dashboard' : 'Create an account to start your AI training'}
                    </p>
                </div>

                <form method="POST" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }} autoComplete="off">
                    {/* Visual honey-pot / trap for autofill to prevent it from targeting the main fields */}
                    <input type="password" style={{ display: 'none' }} tabIndex={-1} autoComplete="new-password" />

                    <div className="form-group-fancy">
                        <label>Username</label>
                        <div style={{ position: 'relative' }}>
                            <Mail size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="text"
                                name="user_identifier"
                                className="input-fancy"
                                placeholder="Enter username"
                                style={{ paddingLeft: '3rem' }}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    {/* Registration Fields */}
                    {!isLoginView && (
                        <>
                            <div className="form-group-fancy">
                                <label>Full Name</label>
                                <div style={{ position: 'relative' }}>
                                    <UserPlus size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input
                                        type="text"
                                        name="full_name"
                                        className="input-fancy"
                                        placeholder="e.g. Juan Dela Cruz"
                                        style={{ paddingLeft: '3rem' }}
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="form-group-fancy">
                                <label>Team / Group Name</label>
                                <div style={{ position: 'relative' }}>
                                    <ShieldCheck size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input
                                        type="text"
                                        name="team_name"
                                        className="input-fancy"
                                        placeholder="e.g. Sales Team Alpha"
                                        style={{ paddingLeft: '3rem' }}
                                        value={teamName}
                                        onChange={(e) => setTeamName(e.target.value)}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-group-fancy">
                        <label>Password</label>
                        <div style={{ position: 'relative' }}>
                            <Lock size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="password"
                                name="user_secret"
                                className="input-fancy"
                                placeholder="••••••••"
                                style={{ paddingLeft: '3rem' }}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                        <input
                            type="checkbox"
                            style={{ width: '1.25rem', height: '1.25rem', marginTop: '0.2rem', cursor: 'pointer', accentColor: 'var(--primary)' }}
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                        />
                        <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            I agree to the <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Terms of Service</span> and understand my data is processed for AI coaching.
                        </label>
                    </div>

                    {error && (
                        <div className="fb-point fb-imp" style={{ padding: '0.75rem 1rem' }}>
                            <AlertCircle size={16} />
                            <span style={{ fontSize: '0.85rem' }}>{error}</span>
                        </div>
                    )}

                    <button type="submit" className="button button-primary" style={{ width: '100%', padding: '0.85rem' }} disabled={loading}>
                        {loading ? 'Processing...' : (isLoginView ? 'Sign In' : 'Create Account')}
                    </button>

                    <p style={{ textAlign: 'center', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                        {isLoginView ? "Don't have an account? " : "Already have an account? "}
                        <button onClick={toggleAuthView} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                            {isLoginView ? 'Register' : 'Login'}
                        </button>
                    </p>
                </form>
            </div>

            <div style={{ marginTop: '2rem', textAlign: 'center', opacity: 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    <ShieldCheck size={14} />
                    Powered by Simulate 2 by Inspiro
                </div>
            </div>
        </div>
    );
};
